<!-- src/components/modal/goal-tracking-modal.vue -->
<template>
  <!-- Add Goal Tracking -->
  <div
    class="modal fade"
    id="new_goal_tracking"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-activity"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Add Goal Tracking</h4>
              <div class="dq-subtitle">
                Track goal progress with dates and targets.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-modal-body">
            <div class="dq-info-row">
              <div class="dq-info-pill">
                <i class="ti ti-search"></i>
                Search goal types quickly
              </div>
              <div class="dq-info-pill">
                <i class="ti ti-calendar"></i>
                Dates are saved as YYYY-MM-DD
              </div>
            </div>

            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-clipboard-text"></i> Goal Tracking Details
                </div>
                <div class="dq-card-sub">
                  Pick goal type, define target, set timeline.
                </div>
              </div>

              <div class="row g-3">
                <!-- Goal Type (Searchable dropdown) -->
                <div class="col-12">
                  <label class="form-label dq-label"
                    >Goal Type <span class="text-danger">*</span></label
                  >

                  <div class="dropdown w-100" ref="gtDdAdd">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-target"></i
                        ></span>
                        <span class="dq-dd-title">
                          {{ selectedGoalTypeLabel || "Select goal type" }}
                        </span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="goalTypeQuery"
                            class="form-control dq-input"
                            placeholder="Search goal type…"
                            @keydown.down.prevent="moveGoalTypeActive(1)"
                            @keydown.up.prevent="moveGoalTypeActive(-1)"
                            @keydown.enter.prevent="selectActiveGoalType()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredGoalTypes"
                          :key="opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === goalTypeActiveIndex }"
                          @mouseenter="goalTypeActiveIndex = idx"
                          @click="pickGoalType(opt)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-target"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Goal type</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredGoalTypes.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No goal types found.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">Start typing to filter goal types.</div>
                </div>

                <!-- Subject -->
                <div class="col-12">
                  <label class="form-label dq-label"
                    >Subject <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.trim="form.subject"
                    class="form-control dq-input"
                    type="text"
                    placeholder="e.g. Increase monthly sales"
                  />
                </div>

                <!-- Target Achievement -->
                <div class="col-12">
                  <label class="form-label dq-label">Target Achievement</label>
                  <input
                    v-model.trim="form.target_achievement"
                    class="form-control dq-input"
                    type="text"
                    placeholder="e.g. 20% growth"
                  />
                  <div class="dq-help">
                    Use numbers, percentages, or a clear measurable target.
                  </div>
                </div>

                <!-- Dates -->
                <div class="col-12">
                  <label class="form-label dq-label">Start Date</label>
                  <date-picker
                    v-model="form.start_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">End Date</label>
                  <date-picker
                    v-model="form.end_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <!-- Description -->
                <div class="col-12">
                  <label class="form-label dq-label">Description</label>
                  <textarea
                    v-model.trim="form.description"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Optional notes…"
                  ></textarea>
                </div>

                <!-- Status -->
                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                   <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />
                  <div class="mt-2 d-flex flex-wrap gap-2">
                    <span
                      v-if="form.status === 'Active'"
                      class="dq-chip dq-chip-ok"
                    >
                      <i class="ti ti-circle-check"></i> Active
                    </span>
                    <span v-else class="dq-chip dq-chip-muted">
                      <i class="ti ti-circle-minus"></i> Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="addingGoalTracking"
            >
              <span
                v-if="addingGoalTracking"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Add Goal Tracking
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Goal Tracking -->
  <div
    class="modal fade"
    id="edit_goal_tracking"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Edit Goal Tracking</h4>
              <div class="dq-subtitle">
                Update goal type, targets, and timeline.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-adjustments"></i> Update Details
                </div>
                <div class="dq-card-sub">
                  Changes apply immediately after saving.
                </div>
              </div>

              <div class="row g-3">
                <!-- Goal Type (Searchable dropdown) -->
                <div class="col-12">
                  <label class="form-label dq-label"
                    >Goal Type <span class="text-danger">*</span></label
                  >

                  <div class="dropdown w-100" ref="gtDdEdit">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-target"></i
                        ></span>
                        <span class="dq-dd-title">
                          {{ selectedGoalTypeLabel || "Select goal type" }}
                        </span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="goalTypeQuery"
                            class="form-control dq-input"
                            placeholder="Search goal type…"
                            @keydown.down.prevent="moveGoalTypeActive(1)"
                            @keydown.up.prevent="moveGoalTypeActive(-1)"
                            @keydown.enter.prevent="selectActiveGoalType()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredGoalTypes"
                          :key="opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === goalTypeActiveIndex }"
                          @mouseenter="goalTypeActiveIndex = idx"
                          @click="pickGoalType(opt)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-target"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Goal type</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredGoalTypes.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No goal types found.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Subject -->
                <div class="col-12">
                  <label class="form-label dq-label"
                    >Subject <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.trim="form.subject"
                    class="form-control dq-input"
                    type="text"
                    placeholder="e.g. Increase monthly sales"
                  />
                </div>

                <!-- Target Achievement -->
                <div class="col-12">
                  <label class="form-label dq-label">Target Achievement</label>
                  <input
                    v-model.trim="form.target_achievement"
                    class="form-control dq-input"
                    type="text"
                    placeholder="e.g. 20% growth"
                  />
                </div>

                <!-- Dates -->
                <div class="col-12">
                  <label class="form-label dq-label">Start Date</label>
                  <date-picker
                    v-model="form.start_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">End Date</label>
                  <date-picker
                    v-model="form.end_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <!-- Description -->
                <div class="col-12">
                  <label class="form-label dq-label">Description</label>
                  <textarea
                    v-model.trim="form.description"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Optional notes…"
                  ></textarea>
                </div>

                <!-- Status -->
                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                   <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />

                  <div class="mt-2 d-flex flex-wrap gap-2">
                    <span
                      v-if="form.status === 'Active'"
                      class="dq-chip dq-chip-ok"
                    >
                      <i class="ti ti-circle-check"></i> Active
                    </span>
                    <span v-else class="dq-chip dq-chip-muted">
                      <i class="ti ti-circle-minus"></i> Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="!isEdit || addingGoalTracking"
            >
              <span
                v-if="addingGoalTracking"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Confirm Delete</h4>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-triangle"></i>
            <div>
              <div class="dq-danger-title">Delete goal tracking record?</div>
              <div class="dq-danger-sub">
                You’re about to permanently remove this goal tracking entry.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>

          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useGoalTrackingStore } from "@/stores/goalTrackingStore";
import { useGoalTypeStore } from "@/stores/goalTypeStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const goalStore = useGoalTrackingStore();
const typeStore = useGoalTypeStore();

const deleting = ref(false);
const addingGoalTracking = computed(() => goalStore.loading);
const dateFormat = "dd/MM/yyyy";

const addKey = ref(0);
const editKey = ref(0);

const form = ref(blankForm());
function blankForm() {
  return {
    goal_id: "",
    subject: "",
    target_achievement: "",
    start_date: null,
    end_date: null,
    description: "",
    status: "Active",
  };
}
function resetForm() {
  const blank = blankForm();
  for (const k in blank) form.value[k] = blank[k];
  goalTypeQuery.value = "";
  goalTypeActiveIndex.value = 0;
}

/* ---------------------------
   Searchable Goal Type dropdown
---------------------------- */
const goalTypeQuery = ref("");
const goalTypeActiveIndex = ref(0);

const goalTypesForSelect = computed(() =>
  (typeStore.items || []).map((t) => ({
    id: t.id,
    label: t.goal_type || "-",
  }))
);

const filteredGoalTypes = computed(() => {
  const q = goalTypeQuery.value.trim().toLowerCase();
  if (!q) return goalTypesForSelect.value;
  return goalTypesForSelect.value.filter((x) =>
    (x.label || "").toLowerCase().includes(q)
  );
});

const selectedGoalTypeLabel = computed(() => {
  const id = form.value.goal_id;
  if (!id) return "";
  const found = goalTypesForSelect.value.find(
    (x) => String(x.id) === String(id)
  );
  return found?.label || "";
});

function clampGoalTypeIndex() {
  const len = filteredGoalTypes.value.length;
  if (!len) {
    goalTypeActiveIndex.value = 0;
    return;
  }
  goalTypeActiveIndex.value = Math.max(
    0,
    Math.min(goalTypeActiveIndex.value, len - 1)
  );
}

function moveGoalTypeActive(dir) {
  const len = filteredGoalTypes.value.length;
  if (!len) return;
  goalTypeActiveIndex.value = (goalTypeActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}

function pickGoalType(opt) {
  form.value.goal_id = opt?.id ?? "";
  goalTypeQuery.value = "";
  goalTypeActiveIndex.value = 0;
  // close bootstrap dropdown
  document.activeElement?.blur?.();
}

function selectActiveGoalType() {
  const opt = filteredGoalTypes.value[goalTypeActiveIndex.value];
  if (opt) pickGoalType(opt);
}

async function scrollActiveIntoView() {
  await nextTick();
  const menus = document.querySelectorAll(".dq-dd-menu .dq-search-list");
  // pick the one currently visible
  const list = Array.from(menus).find((el) => el.offsetParent !== null);
  if (!list) return;
  const active = list.querySelector(".dropdown-item.active");
  active?.scrollIntoView({ block: "nearest" });
}

/* ---------------------------
   Watch edit item
---------------------------- */
watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.goal_id = val.goal_id || "";
      form.value.subject = val.subject || "";
      form.value.target_achievement = val.target_achievement || "";
      form.value.start_date = val.start_date ? new Date(val.start_date) : null;
      form.value.end_date = val.end_date ? new Date(val.end_date) : null;
      form.value.description = val.description || "";
      form.value.status = val.status || "Active";
      goalTypeQuery.value = "";
      goalTypeActiveIndex.value = 0;
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

/* ---------------------------
   Date helper
---------------------------- */
function toYMD(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

/* ---------------------------
   Submit handlers
---------------------------- */
async function submitCreate() {
  if (!form.value.goal_id || !form.value.subject?.trim()) {
    toast("Goal Type and Subject are required", "error");
    return;
  }
  const payload = {
    ...form.value,
    start_date: toYMD(form.value.start_date),
    end_date: toYMD(form.value.end_date),
  };
  await goalStore.create(payload);
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;

  if (!form.value.goal_id || !form.value.subject?.trim()) {
    toast("Goal Type and Subject are required", "error");
    return;
  }

  const payload = {
    ...form.value,
    start_date: toYMD(form.value.start_date),
    end_date: toYMD(form.value.end_date),
  };
  await goalStore.update(props.editItem.id, payload);
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document
    .querySelector('#new_goal_tracking [data-bs-dismiss="modal"]')
    ?.click();
}
function closeEdit() {
  document
    .querySelector('#edit_goal_tracking [data-bs-dismiss="modal"]')
    ?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await goalStore.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

/* ---------------------------
   Lifecycle
---------------------------- */
let cleanup = null;
onMounted(async () => {
  if (!typeStore.items?.length && typeof typeStore.fetchAll === "function") {
    await typeStore.fetchAll();
  }

  const addEl = document.getElementById("new_goal_tracking");
  const editEl = document.getElementById("edit_goal_tracking");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
    clampGoalTypeIndex();
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
.dq-dialog {
  max-width: 820px;
}

/* Shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}
.dq-modal-body {
  padding: 16px 20px;
}

/* Reliable scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 150px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.14);
  color: rgb(79, 70, 229);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Tips */
.dq-info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.dq-info-pill {
  flex: 1 1 260px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.72);
  font-weight: 800;
  font-size: 0.82rem;
}
.dq-info-pill i {
  color: rgba(99, 102, 241, 0.85);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Dropdown */
.dq-dd-btn {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-dd-menu {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}
.dq-search-list {
  max-height: 280px;
  overflow: auto;
  overscroll-behavior: contain;
}
.dq-dd-item {
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-dd-item:hover {
  background: rgba(15, 23, 42, 0.04);
}
.dq-dd-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.dq-avatar {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.1);
  color: rgb(79, 70, 229);
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-dd-title {
  font-weight: 900;
}
.dq-dd-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted {
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.7);
}
.dq-chip-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

@media (max-width: 576px) {
  .dq-dialog {
    max-width: 96vw;
    margin: 0.75rem auto;
  }
}
</style>
